import {Reactivity} from "./reactivity";
import {Features} from "./api";
import {TemplateSyntax} from "./template-syntax";
import {Utility} from "./utility";
import {router} from "./router";
import {Animation} from "./animations";
import {errorHandler} from "../errors/errorHandler";

export const SFV = {

    /**
     * Initializes the SFV processes
     * @param {String} FILE_URL
     * @param {Object} PROPS
     * @constructor
     */
    Initialize: async (FILE_URL, PROPS) => {

        // if props where passed, set them up
        if (PROPS) $ActiveJS.PassedProps = PROPS;

        // get the template view file
        await SFV.extractViewData(FILE_URL);
    },

    /**
     * Fetches the TEXT from a file
     * @param {String} url
     * @returns {Document}
     */
    requestViewTemplate: async (url) => {
        return fetch(url).then(async response => {
            if (response.ok) {
                return response.text();
            }
            else {
                // render a stipulated 404 page
                return await SFV.fetchDefaultPageNotFound();
            }
        })
        .then((text) => {
            // return the document body
            return Utility.convertTextToDocument(text);
        })
        .catch((err) => {
            // handle errors
            throw err;
        });
    },

    /**
     * Gets the Defaulted
     * @returns {string|Promise<* | void>}
     */
    fetchDefaultPageNotFound: () => {
        if ($ActiveJS.pageNotFound !== false) {
            return fetch($ActiveJS.pageNotFound).then(response => {
                if (response.ok) {
                    return response.text();
                }
            })
            .catch((err) => {
                // handle errors
                throw err;
            });
        }
        else {
            return Utility.getDefault404HTML();
        }
    },

    /**
     * Will extract the script tag and append to the DOM body
     * @returns {Promise<void>}
     */
    extractViewData: async (FILE_URL) => {

        // get the template
        $ActiveJS['Template'] = await SFV.requestViewTemplate(FILE_URL);

        // build up a script
        let newScript = document.createElement("script");
        newScript.type = 'module';
        newScript.id = "CURRENT_VM";

        // add JS to the script
        let inlineScript
        if ($ActiveJS['Template']) {
            // get the script tag from the returned document object
            const VIEW_SCRIPT = $ActiveJS['Template'].getElementsByTagName("script");
            
            // make sure that it found an element
            if (VIEW_SCRIPT.length > 0) {

                // if a src value is found, make request to that file to get it's contents
                if (VIEW_SCRIPT[0].src != "" && VIEW_SCRIPT[0].src != undefined) {
                    // get the template
                    $ActiveJS['Script'] = await SFV.requestViewTemplate(VIEW_SCRIPT[0].src);
                    inlineScript = document.createTextNode($ActiveJS['Script'].body.innerHTML);
                }
                else inlineScript = document.createTextNode(VIEW_SCRIPT[0].innerHTML);
            }
        }
        
        // add view js to the newly created script tag
        if (inlineScript !== undefined) {
            const oldScript = document.getElementById('CURRENT_VM');
            if (oldScript) {
                oldScript.remove();
                newScript.appendChild(inlineScript);
            }
            else newScript.appendChild(inlineScript);
        }

        // append the newly created script tag to the actual DOM
        document.body.appendChild(newScript);

        // now remove the VM code so it's not visible to the user
        const CURRENT_VM_ELEMENT = document.querySelector('#CURRENT_VM');
        if (CURRENT_VM_ELEMENT) CURRENT_VM_ELEMENT.innerHTML = "";
    },

    /**
     * Strips out the Template and Styles and pareses through them for directives and interpolation
     * @param Template
     * @returns {Promise<unknown>}
     */
    prepareTemplate: async (Template) => {
        return new Promise(async resolve => {

            //* get temp and styles
            const VIEW_TEMP = Template.getElementsByTagName("template")[0].innerHTML;
            const VIEW_STYLE = Template.getElementsByTagName("style")[0];

            // prepare the view wrapper DIV
            let ViewWrapper = document.createElement("div");
            ViewWrapper.id = Features.VM.viewWrapperId;
            ViewWrapper.classList.add('view');

            // prepare the view styles
            let style = document.createElement("style");
            style.innerHTML = VIEW_STYLE.innerHTML;
            ViewWrapper.prepend(style);
            ViewWrapper.append(Utility.convertTextToDocument(VIEW_TEMP).body);

            // add all together
            const Document = Utility.convertTextToDocument(ViewWrapper.outerHTML);

            // search through the template for any directives
            await Reactivity.searchTemplateForDirectives(Document);

            // insert the reactive values into their placeholders
            Reactivity.insertReactiveValues(Document);

            // add all events to the template
            TemplateSyntax.OnEvents(Document);

            resolve(Document);
        })
    },

    /**
     * Will prepare the Template for render and setup the initial values for interpolated values, then render
     * @returns {Promise<void>}
     */
    startRenderProcess: async () => {
        // prepare the template for render
        const PreparedTemplate = await SFV.prepareTemplate($ActiveJS['Template']);

        // apply any updates to the template needed
        Reactivity.ApplyDomUpdates({
            template: PreparedTemplate,
            loadedVM: Features.VM,
            propertyToUpdate: false,
        });

        // get the root render Div
        const ROOT = document.getElementById(window.$ActiveJS.renderEl);

        // render template to render Element
        if (router.currentRoute.animate !== undefined && router.currentRoute.animate !== false) {
            Animation.initialize({
                Root: ROOT,
                routeData: router.currentRoute,
                navBack: router.options.isNavBack,
                Element: PreparedTemplate.body.firstChild,
            });
        }
        else {
            ROOT.innerHTML = "";
            ROOT.appendChild(PreparedTemplate.body.firstChild);
            $ActiveJS.initialRenderFinished = true;
        }


        // rendered callback for views
        if (Features.LifeCycles["Rendered"]) {
            Features.LifeCycles["Rendered"]();
            Features.LifeCycles["Rendered"] = false;
        }

        // check for element animations
        await Reactivity.checkForElementAnimations();

        // check for any system errors
        errorHandler.generateVisualErrorLog();
    },

};